<script setup lang="ts">
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import { useTableStore } from '@/stores/tableStore.ts'
import type Alliance from '@/types/Alliance.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import PlayerName from '@/components/Objects/PlayerName.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import ActionBarBlock from '@/components/Interface/DataTable/ActionBar/ActionBarBlock.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const tableStore = useTableStore()
const allianceStore = useAllianceStore()
const gameStore = useGameStore()
const router = useRouter()

// Filter function for the table
const filters = (alliances: Alliance[]) => {
  return alliances.filter((alliance) => {
    // Filter out deleted alliances
    if (alliance.deleted) {
      return false
    }

    // If no search query, return all non-deleted
    if (
      !tableStore.alliances.filters.nameSearch ||
      tableStore.alliances.filters.nameSearch === ''
    ) {
      return true
    }
    // Otherwise, filter based on the alliance name (case-insensitive search)
    return alliance.name
      .toLowerCase()
      .includes(tableStore.alliances.filters.nameSearch.toLowerCase())
  })
}

// Function to view alliance details - navigates to alliance page for members/leaders, opens modal for others
const viewAllianceDetails = (id: number) => {
  // Check if player is a member of this alliance
  if (gameStore.player?.allianceId === id) {
    // Use router to navigate to the alliance page
    router.push('/diplomacy/alliance')
  } else {
    // Open modal for viewing other alliances
    gameStore.modalType = 'viewAlliance'
    gameStore.modalData = { allianceId: id }
  }
}

const createAlliance = () => {
  gameStore.modalType = 'createAlliance'
  gameStore.modalData = {}
}
</script>

<template>
  <div class="px-2 md:px-0 pt-1">
    <FerionDataTable
      :data="allianceStore.alliances"
      tableStoreName="alliances"
      :selector="false"
      :filters="filters"
      :heightLimit="'h-[calc(100vh-9.2rem)]'"
      :click="viewAllianceDetails"
    >
      <!-- Action Bar -->
      <template #actionBar>
        <ActionBarBlock class="mr-1 flex items-center px-1 rounded">
          <ActionButton
            :class="{
              'my-1': true,
              'opacity-50 cursor-not-allowed': !!gameStore.player?.allianceId
            }"
            @click="createAlliance"
            :disabled="!!gameStore.player?.allianceId"
          >
            {{ !!gameStore.player?.allianceId ? t('diplomacy.alliance.notAvailable') : t('diplomacy.list.create') }}
          </ActionButton>
        </ActionBarBlock>
        <ActionBarFiller class="mr-1 rounded"></ActionBarFiller>
        <ActionBarSearch
          v-model="tableStore.alliances.filters.nameSearch"
          :name="t('diplomacy.list.name')"
          :placeHolder="t('diplomacy.list.search')"
          class="rounded"
        />
      </template>

      <!-- Column Headers -->
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderFixedWidthIcon
          width="3"
          column="allianceImage"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        />
        <HeaderTextFlex
          min-width="12"
          column="name"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          {{ t('diplomacy.list.name') }}
        </HeaderTextFlex>
        <HeaderFixedWidthText
          width="12"
          column="leaderName"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          {{ t('diplomacy.list.leader') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="8"
          column="memberCount"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          {{ t('diplomacy.list.members') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="10"
          column="status"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          {{ t('diplomacy.list.status') }}
        </HeaderFixedWidthText>
      </template>

      <!-- Row Data -->
      <template #rows="{ item }">
        <CellFixedWidth width="3">
          <img :src="item.allianceImage" class="w-8 h-8 rounded" />
        </CellFixedWidth>
        <CellFlexWidth min-width="12">
          {{ item.name }}
        </CellFlexWidth>
        <CellFixedWidth width="12" align="left">
          <PlayerName :playerId="item.leaderPlayerId" />
        </CellFixedWidth>
        <CellFixedWidth width="8" align="left">
          {{ item.members ? item.members.length : 0 }}
        </CellFixedWidth>
        <CellFixedWidth width="10" align="left">
          <span v-if="item.leaderPlayerId === gameStore.player?.id" class="text-green-500"
            >{{ t('diplomacy.alliance.members.leader') }}</span
          >
          <span v-else-if="item.members?.includes(gameStore.player?.id)" class="text-metal-300"
            >{{ t('diplomacy.alliance.members.member') }}</span
          >
          <span
            v-else-if="item.requestingToJoin && item.requestingToJoin !== 0"
            class="text-yellow-500"
            >{{ t('diplomacy.list.requested') }}</span
          >
          <span v-else>-</span>
        </CellFixedWidth>
      </template>
    </FerionDataTable>
  </div>
</template>
